/**
 * Architecture formulaires santé adulte / mineur.
 * Contenu TEMP — à aligner sur le formulaire officiel Shotokan AAMC (non inventé comme version finale).
 */
import { HEALTH_FORM_VERSION_ADULT, HEALTH_FORM_VERSION_MINOR } from "./membershipConstants.js";

/** @typedef {'yes_no'|'text'|'tel'|'checkbox'} HealthFieldType */

/**
 * @typedef {Object} HealthFieldDef
 * @property {string} id
 * @property {HealthFieldType} type
 * @property {boolean} required
 * @property {boolean} [officialPending]
 * @property {boolean} [sensitive]
 */

/** @type {HealthFieldDef[]} */
export const ADULT_HEALTH_FIELDS = [
  { id: "hasMedicalConditions", type: "yes_no", required: true, officialPending: true, sensitive: true },
  { id: "medicalConditionsDetails", type: "text", required: false, officialPending: true, sensitive: true },
  { id: "hasAllergies", type: "yes_no", required: true, officialPending: true, sensitive: true },
  { id: "allergiesDetails", type: "text", required: false, officialPending: true, sensitive: true },
  { id: "hasMedications", type: "yes_no", required: true, officialPending: true, sensitive: true },
  { id: "medicationsDetails", type: "text", required: false, officialPending: true, sensitive: true },
  { id: "activityRestrictions", type: "text", required: false, officialPending: true, sensitive: true },
  { id: "physicianName", type: "text", required: false, officialPending: true, sensitive: true },
  { id: "physicianPhone", type: "tel", required: false, officialPending: true, sensitive: true },
  { id: "emergencyCareConsent", type: "checkbox", required: true, officialPending: true, sensitive: false },
  { id: "accuracyAttestation", type: "checkbox", required: true, officialPending: true, sensitive: false },
];

/** @type {HealthFieldDef[]} */
export const MINOR_HEALTH_FIELDS = [
  ...ADULT_HEALTH_FIELDS,
  { id: "immunizationUpToDate", type: "yes_no", required: false, officialPending: true, sensitive: true },
  { id: "parentalHealthAuthority", type: "checkbox", required: true, officialPending: true, sensitive: false },
];

export function getHealthFields(kind) {
  return kind === "minor" ? MINOR_HEALTH_FIELDS : ADULT_HEALTH_FIELDS;
}

export function getHealthFormVersion(kind) {
  return kind === "minor" ? HEALTH_FORM_VERSION_MINOR : HEALTH_FORM_VERSION_ADULT;
}

/**
 * @param {object} answers
 * @param {'adult'|'minor'} kind
 */
export function isHealthFormComplete(answers, kind) {
  if (!answers || typeof answers !== "object") return false;
  const fields = getHealthFields(kind);
  for (const field of fields) {
    if (!field.required) continue;
    const value = answers[field.id];
    if (field.type === "checkbox") {
      if (value !== true) return false;
    } else if (field.type === "yes_no") {
      if (value !== "yes" && value !== "no") return false;
    } else if (!String(value ?? "").trim()) {
      return false;
    }
  }
  if (answers.hasMedicalConditions === "yes" && !String(answers.medicalConditionsDetails ?? "").trim()) {
    return false;
  }
  if (answers.hasAllergies === "yes" && !String(answers.allergiesDetails ?? "").trim()) {
    return false;
  }
  if (answers.hasMedications === "yes" && !String(answers.medicationsDetails ?? "").trim()) {
    return false;
  }
  return true;
}

export function emptyHealthAnswers() {
  return {
    hasMedicalConditions: "",
    medicalConditionsDetails: "",
    hasAllergies: "",
    allergiesDetails: "",
    hasMedications: "",
    medicationsDetails: "",
    activityRestrictions: "",
    physicianName: "",
    physicianPhone: "",
    emergencyCareConsent: false,
    accuracyAttestation: false,
    immunizationUpToDate: "",
    parentalHealthAuthority: false,
  };
}
